<template>
  <div v-if="!initializing" key="input">
    <soft-autocomplete :disabled="initializing || disabled" :group-by="groupBy" :group-order="groupOrder"
                       :item-color="itemColor || color" :items="computedItems" :loading="loading || initializing"
                       :placeholder="placeholder" :rules="rules" :search-input.sync="searchInput"
                       :suggestion-hint="prefetch ? suggestionHint : ''" :value="value" no-filter
                       v-bind="styleOptions" @change="$emit('user-change', $event)" @input="emitInput"
                       @update:search-input="handleSearch">
      <!-- optional slots of the wrapping picker (e.g. item rows with hover preview), passed through unchanged -->
      <template v-for="(_slot, name) in $scopedSlots" v-slot:[name]="scope">
        <slot :name="name" v-bind="scope"/>
      </template>
    </soft-autocomplete>
  </div>
  <div v-else>
    <soft-autocomplete :item-color="itemColor || color" :placeholder="placeholder" disabled loading
                       v-bind="styleOptions"/>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import mongoose from 'mongoose';
import {InputStyleOptions} from '@/constants/InputStyleOptions.constant';
import {PaginationRequest} from '@/classes/dto/_common/request/PaginationRequest';
import {Constructor} from 'vue/types/options';
import {sleep} from '@/helpers/sleep.helper';
import {Rule} from '@/helpers/ruleFactory.helper';
import {PopulationOptions} from '@/interfaces/PopulationOptions';
import {GeoNearRequest} from '@/classes/dto/_common/request/GeoNearRequest';

import SoftAutocomplete from '@/components/common/autocomplete/SoftAutocomplete.vue';
import {PickerEnricher, PickerItem} from '@/components/common/entity-picker/entityPicker.types';

type ItemType = PickerItem;

/** Items whose text / description contain every word of the search (case- and accent-insensitive) */
const normalize = (value: unknown): string => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
function matchingItems<T extends { text?: unknown; description?: unknown }>(items: T[], search: unknown): T[] {
  const words = normalize(search).split(/\s+/).filter(Boolean);
  if (!words.length) {
    return items;
  }
  return items.filter((item) => {
    const haystack = `${normalize(item.text)} ${normalize(item.description)}`;
    return words.every((word) => haystack.includes(word));
  });
}

export default Vue.extend({
  components: {SoftAutocomplete},
  props: {
    ...InputStyleOptions.Autocomplete,
    api: {
      type: Object as () => ({ find: (paginationRequest: PaginationRequest<any>) => Promise<any> }),
      required: true,
    },
    populate: {
      type: Object as () => PopulationOptions,
      required: false,
    },
    respMapper: {
      type: Object as () => ((data: any) => ItemType[]),
      required: true,
    },
    fields: {type: Array as () => string[], required: true},
    prefetch: {type: Boolean, default: false},
    prefetchCount: {type: Number, default: 10},
    placeholder: {type: String, default: 'Tippen um zu suchen'},
    rules: {type: Array as () => Rule[]},
    filter: {type: Object as () => any & { near?: GeoNearRequest }, default: () => ({})},
    prefetchFilter: {type: Object as () => any & { near?: GeoNearRequest }, required: false},
    value: {type: Object as () => Array<mongoose.Types.ObjectId> | mongoose.Types.ObjectId | undefined},
    applyFilterOnInitialization: {type: Boolean, default: false},
    /**
     * Optional asynchronous enrichment (context line, tags, ranking) of the mapped items.
     * Must keep text, value, disabled and meta of every item; may reorder and add presentation fields.
     */
    enrich: {type: Function as unknown as () => PickerEnricher, required: false},
    /** Optional group label per item (presentation only), e.g. "Bewerber:innen" */
    groupBy: {type: Function as unknown as () => (item: PickerItem) => string | null, required: false},
    groupOrder: {type: Array as () => string[], required: false},
    /** Small hint above the prefetched suggestions while the field is empty */
    suggestionHint: {type: String, default: 'Vorschläge – tippen, um zu suchen'},
    /**
     * Keep only search results whose text or description contains every typed word (the server search may match other
     * fields, e.g. login names). Default off.
     */
    matchSearch: {type: Boolean, default: false},
  },
  data: () => ({
    searchInput: 'Eulenbande',
    initializing: true,
    loading: false,
    items: [] as ItemType[],
    recentValueItems: [] as ItemType[],

    throttleCounter: 0,
    throttleTimeout: 500,
    enrichRun: 0,
  }),
  computed: {
    metaKeys(): string[] {
      const metaKeySet = new Set<string>();
      for (const item of this.items) {
        if (item.meta) {
          for (const key of Object.keys(item.meta)) {
            metaKeySet.add(key);
          }
        }
      }
      return Array.from(metaKeySet);
    },
    styleOptions(): Record<string, { type: Constructor, required?: boolean }> {
      const props = this.$props as Record<string, any>;
      const styleOptions = {} as Record<string, { type: Constructor, required?: boolean }>;
      for (const key of Object.keys(props)) {
        if (InputStyleOptions.Autocomplete.hasOwnProperty(key)) {
          styleOptions[key] = props[key];
        }
      }
      return styleOptions;
    },
    style(): Record<string, any> {
      let value = 86;
      if (this.dense) {
        value -= 20;
      }
      if (this.hideDetails) {
        value -= 26;
      }
      return {
        minHeight: `${value}px`,
      };
    },
    computedFilter(): any {
      if (this.filter) {
        return {...this.filter, $search: this.searchInput};
      } else {
        return {$search: this.searchInput};
      }
    },
    computedItems(): ItemType[] {
      const stringItemValues = this.items.map((item) => item.value.toString());
      const filteredRecentValueItems = this.recentValueItems.filter((item) => !stringItemValues.includes(item.value.toString()));
      return [...this.items, ...filteredRecentValueItems];
    },
    searchInputValid(): boolean {
      return !!(this.searchInput && !this.computedItems.find((item) => item.text === this.searchInput));
    },
  },
  methods: {
    /** Shows the mapped items immediately and replaces them with the enriched ones once loaded */
    setItems(items: ItemType[]) {
      this.items = items;
      this.runEnrichment();
    },
    async runEnrichment() {
      if (!this.enrich) {
        return;
      }
      const run = ++this.enrichRun;
      const items = this.items;
      const recentValueItems = this.recentValueItems;
      try {
        const [enrichedItems, enrichedRecent] = await Promise.all([
          items.length ? this.enrich(items) : Promise.resolve(items),
          recentValueItems.length ? this.enrich(recentValueItems) : Promise.resolve(recentValueItems),
        ]);
        if (run !== this.enrichRun) {
          return;
        }
        if (this.items === items) {
          this.items = enrichedItems;
        }
        if (this.recentValueItems === recentValueItems) {
          this.recentValueItems = enrichedRecent;
        }
      } catch (e) {
        // Context is a hint only – the plain items stay usable
      }
    },
    emitInput(value: any) {
      if (value) {
        const values = Array.isArray(value) ? value : [value];
        const stringValues = values.map((value) => value.toString());
        const currentItems = this.items.filter((item) => stringValues.includes(item.value.toString()));
        currentItems.sort((a, b) => stringValues.indexOf(a.value.toString()) - stringValues.indexOf(b.value.toString()));
        const response: Record<string, any[]> = {};
        for (const key of this.metaKeys) {
          response[key] = [];
        }
        for (const item of currentItems) {
          const itemMeta = item.meta || {};
          for (const key of this.metaKeys) {
            if (itemMeta[key]) {
              response[key].push(itemMeta[key]);
            } else {
              response[key].push(undefined);
            }
          }
        }
        for (const key of this.metaKeys) {
          const values =  response[key];
          if (this.multiple) {
            this.$emit(`update:${key}`, values);
          } else {
            this.$emit(`update:${key}`, values[0]);
          }
        }
      } else {
        for (const key of this.metaKeys) {
          this.$emit(`update:${key}`, this.multiple ? [] : undefined);
        }
      }
      this.$emit('input', value);
    },
    async handlePrefetch() {
      try {
        const resp = await this.api.find({
          filter: this.prefetchFilter || this.filter,
          skipPagination: this.prefetchFilter ? !!this.prefetchFilter.near : !!this.filter.near,
          limit: Number(this.prefetchCount),
          fields: this.fields,
          populate: this.populate,
        });
        this.setItems(this.respMapper(resp));
        // The suggestions shown without typing (e.g. for a quick pick / auto-selection by the wrapping picker)
        this.$emit('prefetched', this.items);
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Daten', 'error', e);
      } finally {
        this.initializing = false;
      }
    },
    async handleSearch() {
      if (!this.searchInputValid) {
        if (!this.searchInput && this.prefetch) {
          await this.handlePrefetch();
        }
        return;
      }
      this.loading = true;
      this.throttleCounter++;
      await sleep(this.throttleTimeout);
      this.throttleCounter--;
      if (this.throttleCounter > 0) {
        return;
      } else if (!this.searchInputValid) {
        this.loading = false;
        return;
      }
      // Handle search
      try {
        const resp = await this.api.find({
          filter: this.computedFilter,
          limit: 20,
          fields: this.fields,
          populate: this.populate,
          skipPagination: !!this.computedFilter.near,
        });
        const mapped = this.respMapper(resp);
        this.setItems(this.matchSearch ? matchingItems(mapped, this.searchInput) : mapped);
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Daten', 'error', e);
      } finally {
        this.loading = false;
      }
    },
    async initialize() {
      this.initializing = true;
      this.items = [];
      this.recentValueItems = [];
      if (!this.value && this.multiple) {
        this.$emit('input', []);
      } else if (this.multiple && !Array.isArray(this.value)) {
        this.$emit('input', [this.value]);
      } else if (!this.multiple && Array.isArray(this.value)) {
        this.$emit('input', this.value[0]);
      }

      if (this.value) {
        const idArray = Array.isArray(this.value) ? this.value : [this.value];
        try {
          const resp = await this.api.find({
            filter: this.applyFilterOnInitialization ? { ...this.filter, _id: idArray } :{_id: idArray},
            fields: this.fields,
            populate: this.populate,
          });
          this.recentValueItems = this.respMapper(resp);
          if (!this.prefetch) {
            this.runEnrichment();
          }
          if (this.applyFilterOnInitialization) {
            const itemValues = this.recentValueItems.map((item) => item.value.toString());
            if (this.multiple) {
              this.$emit('input', itemValues);
            } else {
              this.$emit('input', itemValues[0]);
            }
          }
        } catch (e) {
          this.$$showSnackbar('Fehler beim Initialisieren der existierenden Daten', 'error', e);
          for (const id of idArray) {
            this.recentValueItems.push({text: 'Unbekanntes Element', value: id});
          }
        }
      }

      if (this.prefetch) {
        await this.handlePrefetch();
      }
      this.initializing = false;
    },
  },
  watch: {
    enrich() {
      this.runEnrichment();
    },
    searchInput(v) {
      if (!v && this.value && !Array.isArray(this.value)) {
        const stringValue = this.value.toString();
        const item = this.computedItems.find((item) => stringValue.includes(item.value.toString()));
        if (item) {
          this.searchInput = item.text;
        }
      }
    },
    filter: {
      deep: true,
      handler() {
        this.handleSearch();
      },
    },
    value: {
      deep: true,
      handler() {
        if (this.value) {
          if (Array.isArray(this.value)) {
            if (this.value.length === 0) {
              this.recentValueItems = [];
            }
            const stringValues = this.value.map((value) => value.toString());
            this.recentValueItems = this.computedItems.filter((item: {
              value: any
            }) => stringValues.includes(item.value.toString()));
          } else {
            const stringValue = this.value.toString();
            const item = this.computedItems.find((item) => item.value.toString() === stringValue);
            if (item) {
              this.recentValueItems = [item];
            } else {
              // this.recentValueItems = [{ text: 'Unbekanntes Element', value: this.value }];
              this.initialize();
            }
          }
        } else {
          this.recentValueItems = [];
        }
      },
    },
  },
  mounted() {
    this.initialize();
  },
})
</script>
